import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from '../t';
import C from '../colors';
import { WATERMARK_TEXT_HELP, validateWatermarkText } from '../watermarkTextPolicy';
import {
  WATERMARK_EDITOR_WIDTH,
  getFillWatermarkSpacing,
  getSingleWatermarkCenter,
  getWatermarkPreviewMetrics,
  normalizeWatermarkEditorConfig,
} from '../watermarkPreviewPolicy';

const makePresets = (base) => [
  { id: 'signature', name: '工作室签名', en: 'Signature', text: base,              size: 28, alpha: 0.85, pos: 'bottom-right', style: 'single', shadow: true,  rotate: 0  },
  { id: 'copyright', name: '版权小字',   en: 'Copyright',  text: `© ${base}`,      size: 14, alpha: 0.7,  pos: 'bottom-right', style: 'single', shadow: false, rotate: 0  },
  { id: 'tile',      name: '满铺保护',   en: 'Tiled',      text: base,              size: 22, alpha: 0.18, pos: 'center',       style: 'fill',   shadow: false, rotate: 0 },
];

const RATIOS = ['16:9', '4:3', '1:1', '9:16'];

function Icon({ name, size = 14 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'check') return <svg viewBox="0 0 24 24" {...s} strokeWidth="2"><path d="m5 13 4 4L19 7"/></svg>;
  if (name === 'plus') return <svg viewBox="0 0 24 24" {...s}><path d="M12 5v14M5 12h14"/></svg>;
  return null;
}

function PrimaryBtn({ children, onClick, icon, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '8px 16px', borderRadius: 7,
      background: disabled ? C.hair2 : C.brand, color: '#fff', border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 6,
      boxShadow: disabled ? 'none' : `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
      whiteSpace: 'nowrap',
    }}>{icon && <Icon name={icon} size={14}/>}{children}</button>
  );
}

function GhostBtn({ children, onClick, icon }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '8px 14px', borderRadius: 7,
      background: '#fff', color: C.ink,
      border: `1px solid ${C.hair2}`, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
    }}>{icon && <Icon name={icon} size={14}/>}{children}</button>
  );
}

function Toggle({ value, onChange, onCommit, testId, label, disabled = false }) {
  return (
    <button type="button" data-testid={testId} aria-label={label} aria-pressed={value} disabled={disabled} onClick={() => { onChange(!value); onCommit?.(); }} style={{
      width: 44, height: 24, borderRadius: 12, border: 'none',
      background: value ? C.brand : C.hair2,
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', padding: 0, flexShrink: 0,
      opacity: disabled ? 0.55 : 1,
      transition: 'background .15s',
    }}>
      <span style={{ position: 'absolute', top: 2, left: value ? 20 : 2, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}/>
    </button>
  );
}

function Field({ label, hint, children, inline }) {
  return (
    <div style={{
      padding: '14px 0',
      borderBottom: `1px solid ${C.hair}`,
      display: inline ? 'grid' : 'block',
      gridTemplateColumns: inline ? '1fr auto' : '1fr',
      alignItems: 'center', gap: 14,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 2 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: C.ink3, lineHeight: 1.5 }}>{hint}</div>}
      </div>
      {!inline && <div style={{ marginTop: 10 }}>{children}</div>}
      {inline && <div>{children}</div>}
    </div>
  );
}

function Seg({ value, onChange, onCommit, options, testId }) {
  return (
    <div style={{ display: 'inline-flex', padding: 3, gap: 2, background: C.paper2, border: `1px solid ${C.hair}`, borderRadius: 8 }}>
      {options.map(o => {
        const on = value === o.v;
        return (
          <button key={o.v} type="button" data-testid={testId ? `${testId}-${o.v}` : undefined} aria-pressed={on} onClick={() => { onChange(o.v); onCommit?.(); }} style={{
            padding: '6px 12px', borderRadius: 6, border: 'none',
            background: on ? '#fff' : 'transparent',
            color: on ? C.ink : C.ink3,
            fontFamily: C.sans, fontSize: 12, fontWeight: on ? 600 : 500,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}>{o.l}</button>
        );
      })}
    </div>
  );
}

function Slider({ min, max, step = 1, value, onChange, onCommit, unit, label, defaultValue, testId }) {
  const isDirty = defaultValue != null && value !== defaultValue;
  const reset = () => { onChange(defaultValue); onCommit?.(); };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>{label && `${label} · `}{value}{unit || ''}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isDirty && (
            <button onClick={reset} title="重置" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, color: C.ink3, lineHeight: 1,
              fontSize: 13, opacity: 0.7,
            }}>↺</button>
          )}
          <span style={{ fontFamily: C.mono, fontSize: 10, color: C.ink3, opacity: 0.6 }}>{min}–{max}{unit||''}</span>
        </div>
      </div>
      <input type="range" data-testid={testId} aria-label={label} min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        onMouseUp={onCommit} onTouchEnd={onCommit}
        style={{ width: '100%', accentColor: C.brand }}/>
    </div>
  );
}

function PositionGrid({ value, onChange, onCommit }) {
  const cells = ['top-left','top','top-right','left','center','right','bottom-left','bottom','bottom-right'];
  const posMap = {
    'top-left': 'start start', 'top': 'start center', 'top-right': 'start end',
    'left': 'center start', 'center': 'center center', 'right': 'center end',
    'bottom-left': 'end start', 'bottom': 'end center', 'bottom-right': 'end end',
  };
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 36px)', gridTemplateRows: 'repeat(3, 28px)',
      gap: 3, padding: 5, background: C.paper2, border: `1px solid ${C.hair}`,
      borderRadius: 8, width: 'fit-content',
    }}>
      {cells.map(p => {
        const on = p === value;
        return (
          <button key={p} type="button" data-testid={`watermark-position-${p}`} aria-label={p} aria-pressed={on} onClick={() => { onChange(p); onCommit?.(); }} style={{
            background: on ? C.ink : '#fff',
            border: `1px solid ${on ? C.ink : C.hair2}`, borderRadius: 4, cursor: 'pointer',
            position: 'relative', display: 'grid', placeItems: posMap[p], padding: 4,
          }}>
            <span style={{ width: 8, height: 2.5, borderRadius: 1, background: on ? C.brand : C.hair2 }}/>
          </button>
        );
      })}
    </div>
  );
}

// ── Canvas preview ─────────────────────────────────────────────────

function sampleDraw(ctx, W, H, idx) {
  if (idx === 0) {
    // 风光：蓝天 + 地平线 + 暖色大地
    const sky = ctx.createLinearGradient(0, 0, 0, H * 0.58);
    sky.addColorStop(0, '#2E6FA3'); sky.addColorStop(0.6, '#7BB8D4'); sky.addColorStop(1, '#C9DFE8');
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H * 0.58);
    const land = ctx.createLinearGradient(0, H * 0.58, 0, H);
    land.addColorStop(0, '#8FA866'); land.addColorStop(0.4, '#6D8C42'); land.addColorStop(1, '#3D5220');
    ctx.fillStyle = land; ctx.fillRect(0, H * 0.58, W, H * 0.42);
    // 地平线柔光
    const glow = ctx.createRadialGradient(W * 0.5, H * 0.58, 0, W * 0.5, H * 0.58, W * 0.5);
    glow.addColorStop(0, 'rgba(255,220,140,.28)'); glow.addColorStop(1, 'rgba(255,220,140,0)');
    ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
  } else if (idx === 1) {
    // 人像：浅灰暖调背景 + 中央亮部（模拟浅景深）
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#C8B8A8'); bg.addColorStop(1, '#8A7868');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
    const spot = ctx.createRadialGradient(W * 0.5, H * 0.38, H * 0.05, W * 0.5, H * 0.42, W * 0.55);
    spot.addColorStop(0, 'rgba(255,240,225,.55)'); spot.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = spot; ctx.fillRect(0, 0, W, H);
    const vig = ctx.createRadialGradient(W * 0.5, H * 0.5, H * 0.2, W * 0.5, H * 0.5, W * 0.8);
    vig.addColorStop(0, 'rgba(0,0,0,0)'); vig.addColorStop(1, 'rgba(0,0,0,.35)');
    ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);
  } else {
    // 静物：深色工作台 + 侧光打亮
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#2A2420'); bg.addColorStop(1, '#0E0C0A');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
    const light = ctx.createRadialGradient(W * 0.35, H * 0.3, 0, W * 0.35, H * 0.4, W * 0.6);
    light.addColorStop(0, 'rgba(255,230,180,.62)'); light.addColorStop(0.5, 'rgba(255,200,120,.18)'); light.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = light; ctx.fillRect(0, 0, W, H);
  }
}

const SAMPLE_IMGS = ['/samples/landscape.jpg', '/samples/portrait.jpg', '/samples/still.jpg'];
const imgCache = {};

function loadImg(src) {
  if (imgCache[src]) return Promise.resolve(imgCache[src]);
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => { imgCache[src] = img; res(img); };
    img.onerror = () => res(null);
    img.src = src;
  });
}

function WatermarkPreview({ wm, sampleIdx, ratio }) {
  const ref = useRef(null);
  const baseW = WATERMARK_EDITOR_WIDTH;
  const ratioMap = { '16:9': 405, '4:3': 540, '1:1': 720, '9:16': 1280 };
  // Keep the whole preview visible in the editor. The width limits below are
  // the equivalent of a 52vh / 560px height cap for each aspect ratio.
  const previewWidthMap = {
    '16:9': 'min(100%, 92.445vh, 996px)',
    '4:3': 'min(100%, 69.334vh, 747px)',
    '1:1': 'min(100%, 52vh, 560px)',
    '9:16': 'min(100%, 29.25vh, 315px)',
  };
  const baseH = ratioMap[ratio] || 405;

  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const W = c.width, H = c.height;

    const drawWatermark = () => {
      if (!wm.enabled) return;
      const preview = getWatermarkPreviewMetrics(wm, W);
      ctx.save();
      ctx.font = `400 ${preview.fontSize}px ${preview.fontFamily}`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';

      // OSS shadow_50 is independent from text opacity. A separate Canvas
      // pass preserves that behaviour; the exact blur edge remains specific
      // to the OSS renderer.
      const paintText = (text, x, y) => {
        if (preview.shadow) {
          ctx.save();
          ctx.fillStyle = preview.shadowColor;
          if ('filter' in ctx) ctx.filter = `blur(${preview.shadowBlur}px)`;
          ctx.fillText(text, x + preview.shadowOffsetX, y + preview.shadowOffsetY);
          ctx.restore();
        }
        ctx.fillStyle = `rgba(255,255,255,${preview.alpha})`;
        ctx.fillText(text, x, y);
      };

      if (preview.style === 'fill') {
        ctx.translate(W / 2, H / 2);
        ctx.rotate(preview.rotate * Math.PI / 180);
        const textWidth = ctx.measureText(preview.text).width;
        const { stepX, stepY } = getFillWatermarkSpacing(textWidth, preview.fontSize);
        const diagonal = Math.hypot(W, H);
        for (let y = -diagonal; y <= diagonal; y += stepY)
          for (let x = -diagonal; x <= diagonal; x += stepX)
            paintText(preview.text, x, y);
      } else {
        const metrics = ctx.measureText(preview.text);
        const textHeight = (metrics.actualBoundingBoxAscent || preview.fontSize * 0.8)
          + (metrics.actualBoundingBoxDescent || preview.fontSize * 0.2);
        const center = getSingleWatermarkCenter({
          position: preview.pos,
          canvasWidth: W,
          canvasHeight: H,
          textWidth: metrics.width,
          textHeight,
          inset: preview.inset,
          rotation: preview.rotate,
        });
        ctx.translate(center.x, center.y);
        ctx.rotate(preview.rotate * Math.PI / 180);
        paintText(preview.text, 0, 0);
      }
      ctx.restore();
    };

    ctx.clearRect(0, 0, W, H);
    const src = SAMPLE_IMGS[sampleIdx];
    loadImg(src).then((img) => {
      if (img) {
        const scale = Math.max(W / img.naturalWidth, H / img.naturalHeight);
        const sw = img.naturalWidth * scale, sh = img.naturalHeight * scale;
        ctx.drawImage(img, (W - sw) / 2, (H - sh) / 2, sw, sh);
      } else {
        sampleDraw(ctx, W, H, sampleIdx);
      }
      drawWatermark();
    });
  }, [wm, sampleIdx, ratio]);

  return (
    <div data-testid="watermark-preview-area" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        position: 'relative', borderRadius: 12, overflow: 'hidden',
        border: `1px solid ${C.hair2}`, background: C.ink,
        boxShadow: '0 20px 50px -28px rgba(26,22,20,.45)',
        aspectRatio: `${baseW}/${baseH}`,
        width: previewWidthMap[ratio] || previewWidthMap['16:9'],
        maxHeight: 'min(52vh, 560px)',
        flexShrink: 0,
      }}>
        <canvas data-testid="watermark-preview-canvas" ref={ref} width={baseW} height={baseH} style={{ width: '100%', height: '100%', display: 'block' }}/>
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 4, background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(6px)', fontFamily: C.mono, fontSize: 10, color: 'rgba(255,255,255,.78)' }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: wm.enabled ? C.brand : 'rgba(255,255,255,.4)' }}/>
          {wm.enabled ? 'Live' : 'Off'}
        </div>
      </div>
    </div>
  );
}

// ── Main Editor ────────────────────────────────────────────────────

function WatermarkEditorInner({ wm, onChange, onSave, onAutoSave, onCancel, showActions = true, compact = false, userStore, settingScope = 'order' }) {
  const { t } = useTranslation();
  const [sampleIdx, setSampleIdx] = useState(0);
  const [ratio, setRatio] = useState('16:9');
  const [activePreset, setActivePreset] = useState(null);
  const [textValidation, setTextValidation] = useState(null);
  const [showTextError, setShowTextError] = useState(false);
  const textInputRef = useRef(null);
  const validationSequenceRef = useRef(0);
  const lastValidTextRef = useRef(null);
  const skipValidationEffectRef = useRef(null);
  const latestWmRef = useRef(wm);
  if (latestWmRef.current !== wm) {
    latestWmRef.current = wm;
  }
  const patch = p => {
    const nextWm = { ...latestWmRef.current, ...p };
    latestWmRef.current = nextWm;
    onChange?.(nextWm);
    return nextWm;
  };
  const checkText = useCallback(async (value, showError = true) => {
    const sequence = ++validationSequenceRef.current;
    setTextValidation(null);
    const result = await validateWatermarkText(value);
    if (sequence === validationSequenceRef.current) {
      setTextValidation(result);
      if (showError) setShowTextError(!result.valid);
      if (result.valid) lastValidTextRef.current = result.text;
    }
    return result;
  }, []);

  useEffect(() => {
    const validationKey = `${wm?.enabled ? '1' : '0'}\u0000${wm?.text ?? ''}`;
    if (skipValidationEffectRef.current === validationKey) {
      skipValidationEffectRef.current = null;
      return;
    }
    const hasHistoricalText = String(wm?.text ?? '').length > 0;
    checkText(wm?.text, wm?.enabled || hasHistoricalText);
  }, [wm?.text, wm?.enabled, checkText]);

  const skipNextValidationEffect = (nextWm) => {
    skipValidationEffectRef.current = `${nextWm.enabled ? '1' : '0'}\u0000${nextWm.text ?? ''}`;
  };

  const saveValidated = async (candidate, save) => {
    const result = await checkText(candidate.text, true);
    if (!result.valid) return false;
    const nextWm = normalizeWatermarkEditorConfig({ ...candidate, text: result.text });
    if (result.text !== candidate.text || nextWm.shadow !== candidate.shadow) {
      latestWmRef.current = nextWm;
      skipNextValidationEffect(nextWm);
      onChange?.(nextWm);
    }
    save?.(nextWm);
    return true;
  };

  const commit = () => saveValidated(latestWmRef.current, onAutoSave);

  const handleEnabledChange = async (enabled) => {
    if (!enabled) {
      const result = await checkText(latestWmRef.current.text, true);
      const nextWm = normalizeWatermarkEditorConfig({
        ...latestWmRef.current,
        enabled: false,
        ...(result.valid ? { text: result.text } : {}),
      });
      latestWmRef.current = nextWm;
      onChange?.(nextWm);
      skipNextValidationEffect(nextWm);
      // Closing must always remain possible. If the user just cleared a
      // previously valid value, keep that value on the server while leaving
      // the invalid draft visible locally for correction.
      const savedWm = !result.valid && lastValidTextRef.current
        ? { ...nextWm, text: lastValidTextRef.current }
        : nextWm;
      onAutoSave?.(savedWm);
      return;
    }
    const result = await checkText(latestWmRef.current.text, true);
    if (!result.valid) {
      textInputRef.current?.focus();
      return;
    }
    const nextWm = normalizeWatermarkEditorConfig({ ...latestWmRef.current, enabled: true, text: result.text });
    latestWmRef.current = nextWm;
    onChange?.(nextWm);
    skipNextValidationEffect(nextWm);
    onAutoSave?.(nextWm);
  };

  const handleTextChange = (event) => {
    const value = event.target.value;
    setActivePreset(null);
    setShowTextError(true);
    const nextWm = patch({ text: value });
    skipNextValidationEffect(nextWm);
    checkText(value, true);
  };

  const baseText = userStore?.watermark || wm?.text || 'Studio';
  const PRESETS = makePresets(baseText);

  const applyPreset = async preset => {
    const result = await checkText(preset.text, true);
    if (!result.valid) {
      const nextWm = patch({ text: preset.text });
      skipNextValidationEffect(nextWm);
      setActivePreset(null);
      textInputRef.current?.focus();
      return;
    }
    const nextWm = normalizeWatermarkEditorConfig({ ...latestWmRef.current, ...preset, text: result.text, enabled: true });
    latestWmRef.current = nextWm;
    skipNextValidationEffect(nextWm);
    setActivePreset(preset.id);
    onChange?.(nextWm);
    onAutoSave?.(nextWm);
  };

  const handleStyleChange = (style) => {
    const previous = latestWmRef.current;
    const rotate = previous.rotate ?? 0;
    patch({
      style,
      rotate,
      ...(Number(rotate || 0) !== 0 ? { shadow: false } : {}),
    });
  };

  const handleRotateChange = (rotate) => {
    const wasRotated = Number(latestWmRef.current.rotate || 0) !== 0;
    patch({
      rotate,
      ...(Number(rotate || 0) !== 0 || wasRotated ? { shadow: false } : {}),
    });
  };
  const isRotated = Number(wm.rotate || 0) !== 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: compact ? '16px 24px 80px' : '28px 36px 120px' }}>

          {/* Presets */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 2px 8px' }}>
              <h3 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 18, color: C.ink }}>预设</h3>
              <span style={{ fontFamily: C.mono, fontSize: 10, color: C.ink3 }}>点击一键应用</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)', gap: 8 }}>
              {PRESETS.map(p => {
                const on = activePreset === p.id;
                return (
                  <button key={p.id} data-testid={`watermark-preset-${p.id}`} onClick={() => applyPreset(p)} style={{
                    padding: '12px 14px', background: '#fff', border: `1px solid ${on ? C.brand : C.hair}`, borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: C.sans, transition: 'all .15s', display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{p.name}</span>
                      {on && <span style={{ width: 6, height: 6, borderRadius: 3, background: C.brand, flexShrink: 0 }}/>}
                    </div>
                    <span style={{ fontFamily: C.mono, fontSize: 10, color: C.ink3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{p.en}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview + controls */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
            {/* Preview */}
            <section>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 20, color: C.ink }}>实时预览</h3>
                <Seg value={ratio} onChange={setRatio} options={RATIOS.map(r => ({ v: r, l: r }))}/>
              </div>
              <WatermarkPreview wm={wm} sampleIdx={sampleIdx} ratio={ratio}/>
              <div data-testid="watermark-preview-help" style={{ marginTop: 8, fontSize: 11, color: C.ink3, lineHeight: 1.55 }}>
                预览会保持水印的大小、位置、透明度和排列方式；字体字形及投影细节以实际生成图片为准。
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                {[{ l: '风光', e: 'Landscape' }, { l: '人像', e: 'Portrait' }, { l: '静物', e: 'Still' }].map((s, i) => (
                  <button key={i} onClick={() => setSampleIdx(i)} style={{
                    padding: '6px 12px', borderRadius: 6, border: `1px solid ${sampleIdx === i ? C.ink : C.hair2}`,
                    background: sampleIdx === i ? C.ink : '#fff', color: sampleIdx === i ? '#fff' : C.ink2,
                    fontFamily: C.sans, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}><span style={{ width: 5, height: 5, borderRadius: 3, background: sampleIdx === i ? C.brand : C.hair2 }}/>{s.l}</button>
                ))}
              </div>
            </section>

            {/* Controls */}
            <section style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, padding: '4px 20px 16px', opacity: wm.enabled ? 1 : 0.72 }}>
              <Field label={t("OpenWatermark")} hint="关闭后客户将看到无水印预览。" inline>
                <Toggle testId="watermark-enabled" label={t("OpenWatermark")} value={wm.enabled} onChange={handleEnabledChange}/>
              </Field>

              <Field label={t("Watermark")} hint="建议 ≤ 8 个字符。">
                <input
                  ref={textInputRef}
                  data-testid="watermark-text"
                  value={wm.text || ''}
                  onChange={handleTextChange}
                  onBlur={commit}
                  placeholder={t("Watermark")}
                  aria-invalid={showTextError && textValidation && !textValidation.valid ? 'true' : 'false'}
                  aria-describedby={`watermark-text-help${showTextError && textValidation && !textValidation.valid ? ' watermark-text-error' : ''}`}
                  data-validation-state={textValidation ? (textValidation.valid ? 'valid' : 'invalid') : 'checking'}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 14,
                    fontFamily: C.sans, background: '#fff', outline: 'none', color: C.ink,
                    border: `1.5px solid ${showTextError && textValidation && !textValidation.valid ? '#e88c7c' : C.hair2}`,
                  }}/>
                <div id="watermark-text-help" data-testid="watermark-text-help" style={{ marginTop: 7, fontSize: 11, color: C.ink3, lineHeight: 1.55 }}>
                  {WATERMARK_TEXT_HELP}
                </div>
                {showTextError && textValidation && !textValidation.valid && (
                  <div id="watermark-text-error" data-testid="watermark-text-error" role="alert" style={{ marginTop: 5, fontSize: 12, color: '#c2412e', lineHeight: 1.5 }}>
                    {textValidation.message}
                  </div>
                )}
              </Field>

              {wm.enabled && (
                <>
                  <Field label={wm.style === 'single' ? '样式 / 位置' : '样式'}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                      <div>
                        <Seg testId="watermark-style" value={wm.style} onChange={handleStyleChange} onCommit={commit}
                          options={[{ v: 'single', l: t('Single') }, { v: 'fill', l: t('Fill') }]}/>
                        <div style={{ fontSize: 11, color: C.ink3, marginTop: 6 }}>
                          {wm.style === 'fill' ? '满铺平铺于整张图。' : '在指定位置放置单个水印。'}
                        </div>
                      </div>
                      {wm.style === 'single' && (
                        <PositionGrid value={wm.pos} onChange={v => patch({ pos: v })} onCommit={commit}/>
                      )}
                    </div>
                  </Field>

                  <Field label="字号 / 透明度">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Slider testId="watermark-size" min={12} max={100} value={wm.size} onChange={v => patch({ size: v })} onCommit={commit} unit="px" label="字号" defaultValue={36}/>
                      <Slider testId="watermark-alpha" min={5} max={100} value={Math.round(wm.alpha * 100)} onChange={v => patch({ alpha: v / 100 })} onCommit={commit} unit="%" label="透明度" defaultValue={50}/>
                    </div>
                    <div data-testid="watermark-font-help" style={{ marginTop: 8, fontSize: 11, color: C.ink3, lineHeight: 1.55 }}>
                      字体由系统自动匹配：小字号使用黑体风格，大字号使用宋体风格。实际生成效果可能与浏览器预览略有差异。
                    </div>
                  </Field>

                  <Field label="旋转">
                    <Slider testId="watermark-rotate" min={-45} max={45} value={wm.rotate != null ? wm.rotate : 0} onChange={handleRotateChange} onCommit={commit} unit="°" label="旋转" defaultValue={0}/>
                  </Field>

                  {wm.style === 'single' && (
                    <Field label="边距" hint="控制水印与图片边缘的距离。">
                      <Slider testId="watermark-inset" min={0} max={80} value={wm.inset ?? 24} onChange={v => patch({ inset: v })} onCommit={commit} unit="px" label="边距" defaultValue={24}/>
                    </Field>
                  )}

                  <Field
                    label="投影"
                    hint={isRotated
                      ? '由于 OSS 图片处理不支持同时旋转文字和添加投影，当前投影已自动关闭。'
                      : '水印下方添加投影以提升可读性；旋转角度不为 0° 时，OSS 将无法生成投影并会自动关闭。'}
                    inline
                  >
                    <Toggle testId="watermark-shadow" label="投影" value={!isRotated && Boolean(wm.shadow)} disabled={isRotated} onChange={v => patch({ shadow: v })} onCommit={commit}/>
                  </Field>
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Action bar */}
      {showActions && (
        <div style={{ flexShrink: 0, borderTop: `1px solid ${C.hair}`, background: C.paper, padding: '12px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: C.brand }}/>
            <span data-testid="watermark-scope-help">
              {settingScope === 'global'
                ? '此设置将作为新建订单的默认水印，不会自动修改已有订单。'
                : '修改会应用到本订单采用动态处理的客户预览；历史上已经写入图片的水印不会改变。'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {onCancel && <GhostBtn onClick={onCancel}>取消</GhostBtn>}
            {!onAutoSave && onSave && (
              <PrimaryBtn
                icon="check"
                disabled={!textValidation?.valid}
                onClick={() => saveValidated(latestWmRef.current, onSave)}
              >{t("Save")}</PrimaryBtn>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { WatermarkEditorInner };
export default inject('userStore')(observer(WatermarkEditorInner));
